import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { HourlyUsagePattern } from '../../types/admin';

interface HourlyUsageChartProps {
  data: HourlyUsagePattern[];
  title?: string;
  height?: number;
}

export const HourlyUsageChart: React.FC<HourlyUsageChartProps> = ({
  data,
  title = '時間帯別利用パターン',
  height = 300,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // デバッグ: 受信データをログ出力
  console.log('HourlyUsageChart - 受信データ:', {
    data,
    dataLength: data?.length,
    sampleData: data?.slice(0, 5)
  });

  // データが空の場合の処理
  if (!data || data.length === 0) {
    console.warn('HourlyUsageChart - データが空です');
    return (
      <Paper sx={{ p: { xs: 2, sm: 3 }, height: height + (isMobile ? 80 : 100) }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          時間帯別データがありません
        </Typography>
      </Paper>
    );
  }

  // 最大値を取得（バーの高さ正規化用）
  const maxSessions = Math.max(...data.map(d => d.sessionCount), 1);
  console.log('HourlyUsageChart - 最大セッション数:', maxSessions);

  // 時間を表示形式に変換
  const formatHour = (hour: number): string => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  // パーセンテージに基づく色の取得
  const getBarColor = (percentage: number): string => {
    if (percentage >= 8) return theme.palette.error.main;      // 高利用（赤）
    if (percentage >= 5) return theme.palette.warning.main;    // 中利用（橙）
    if (percentage >= 2) return theme.palette.primary.main;    // 普通（青）
    return theme.palette.grey[300];                           // 低利用（灰）
  };

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, height: height + (isMobile ? 80 : 100) }}>
      <Typography 
        variant="h6" 
        gutterBottom
        sx={{ 
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>
      
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-end', 
        justifyContent: 'space-between',
        height: height,
        gap: { xs: 0.5, sm: 1 },
        mt: { xs: 1, sm: 2 },
      }}>
        {data.map((hourData, index) => {
          const barHeight = (hourData.sessionCount / maxSessions) * (height - (isMobile ? 40 : 60));
          
          return (
            <Tooltip
              key={index}
              title={
                <Box>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {formatHour(hourData.hour)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    セッション数: {hourData.sessionCount}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    ユーザー数: {hourData.userCount}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    割合: {hourData.percentage}%
                  </Typography>
                </Box>
              }
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  flex: 1,
                  height: '100%',
                  justifyContent: 'flex-end',
                }}
              >
                {/* バー */}
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: { xs: 12, sm: 20 },
                    height: Math.max(barHeight, 2),
                    backgroundColor: getBarColor(hourData.percentage),
                    borderRadius: '2px 2px 0 0',
                    mb: 1,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      opacity: 0.8,
                      transform: 'scale(1.05)',
                    },
                  }}
                />
                
                {/* 時間ラベル */}
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: { xs: '8px', sm: '10px' },
                    color: 'text.secondary',
                    textAlign: 'center',
                    transform: 'rotate(-45deg)',
                    transformOrigin: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {hourData.hour}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      
      {/* 凡例 */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: { xs: 1, sm: 2 }, 
        mt: { xs: 1, sm: 2 },
        flexWrap: isSmall ? 'wrap' : 'nowrap',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ 
            width: { xs: 8, sm: 12 }, 
            height: { xs: 8, sm: 12 }, 
            backgroundColor: theme.palette.error.main 
          }} />
          <Typography 
            variant="caption"
            sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
          >
            高利用 (8%+)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ 
            width: { xs: 8, sm: 12 }, 
            height: { xs: 8, sm: 12 }, 
            backgroundColor: theme.palette.warning.main 
          }} />
          <Typography 
            variant="caption"
            sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
          >
            中利用 (5-8%)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ 
            width: { xs: 8, sm: 12 }, 
            height: { xs: 8, sm: 12 }, 
            backgroundColor: theme.palette.primary.main 
          }} />
          <Typography 
            variant="caption"
            sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
          >
            普通 (2-5%)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ 
            width: { xs: 8, sm: 12 }, 
            height: { xs: 8, sm: 12 }, 
            backgroundColor: theme.palette.grey[300] 
          }} />
          <Typography 
            variant="caption"
            sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
          >
            低利用 (2%未満)
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}; 